import React, { useState } from "react";
import styles from "./WorkshopDeal.module.css";

import CalculatePV from "../../helpers/calculatePV";
import HemDependant from "../../helpers/hemDependant";
import HemSingleJoint from "../../helpers/hemSingleJoint";
import CalculateNetIncome from "../../helpers/calculateNetIncome";

const SAVE_WEBHOOK = "https://hooks.zapier.com/hooks/catch/XXXXXXX/save/";

function WorkshopDeal({ formData }) {
  const [submitting, setSubmitting] = useState(false);
  const [capacity, setCapacity] = useState(null);

  // --- Borrowing Capacity ---
  const calculateCapacity = () => {
    const applicants = formData.hasApplicant2 ? 2 : 1;

    const incomes = formData.incomeExpenses || [];
    const liabilities =
      formData.assetsLiabilities?.filter((x) => x.type === "Liability") || [];

    const totalIncome = incomes.reduce(
      (acc, row) => acc + Number(row.amount || 0),
      0
    );
    const totalLiabilities = liabilities.reduce(
      (acc, row) => acc + Number(row.value || 0),
      0
    );

    const netIncome = CalculateNetIncome(totalIncome, totalLiabilities);
    const hem = applicants === 2 ? HemSingleJoint(applicants) : HemDependant(0);
    const adjustedIncome = netIncome - hem;

    if (adjustedIncome <= 0) return 0;
    const result = CalculatePV(adjustedIncome, 30, 0.07);
    setCapacity(result);
    return result;
  };

  // --- Send to CRM ---
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const payload = { ...formData, borrowingCapacity: capacity };

      const res = await fetch(SAVE_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (result.success) {
        alert(result.message || "Lead sent to CRM!");
      } else {
        alert("Error: " + result.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error sending data to CRM.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <h3>Workshop Your Deal</h3>

      {/* Applicants */}
      <div className={styles.section}>
        <h4>Applicants</h4>
        <p>
          <strong>Applicant 1:</strong>{" "}
          {formData.applicant1?.name || "—"} /{" "}
          {formData.applicant1?.income ? `$${formData.applicant1.income}` : "—"}
        </p>
        {formData.hasApplicant2 && (
          <p>
            <strong>Applicant 2:</strong>{" "}
            {formData.applicant2?.name || "—"} /{" "}
            {formData.applicant2?.income
              ? `$${formData.applicant2.income}`
              : "—"}
          </p>
        )}
      </div>

      {/* Property */}
      <div className={styles.section}>
        <h4>Property</h4>
        <p>
          {formData.property?.address || "—"} |{" "}
          {formData.property?.type || "—"} |{" "}
          {formData.property?.usage || "—"}
        </p>
        <p>
          Value:{" "}
          {formData.property?.value
            ? `$${formData.property.value}`
            : "—"} | Loan Balance:{" "}
          {formData.property?.loanBalance
            ? `$${formData.property.loanBalance}`
            : "—"}
        </p>
        <p>Lender: {formData.property?.lender || "—"}</p>
      </div>

      {/* Income & Expenses */}
      <div className={styles.section}>
        <h4>Income & Expenses</h4>
        {(formData.incomeExpenses || []).map((row, idx) => (
          <p key={idx}>
            {row.owner} - {row.category}: ${row.amount} ({row.frequency})
          </p>
        ))}
      </div>

      {/* Assets & Liabilities */}
      <div className={styles.section}>
        <h4>Assets & Liabilities</h4>
        {(formData.assetsLiabilities || []).map((row, idx) => (
          <p key={idx}>
            {row.type} - {row.category} ({row.owner}): ${row.value}
          </p>
        ))}
      </div>

      {/* Borrowing Capacity */}
      <div className={styles.section}>
        <h4>Borrowing Capacity</h4>
        {capacity !== null ? (
          <p>
            Estimated: <strong>${capacity.toLocaleString()}</strong>
          </p>
        ) : (
          <button className={styles.calcBtn} onClick={calculateCapacity}>
            Calculate Capacity
          </button>
        )}
      </div>

      {/* Submit */}
      <div className={styles.actions}>
        <button
          className={styles.submitBtn}
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Sending..." : "Send to CRM"}
        </button>
      </div>
    </div>
  );
}

export default WorkshopDeal;
